/**
 * Community plans — the SCAFFOLD for sharing and discovering pilotage plans. The
 * UI is real; the backend is not. Every share/rate/list call goes through the
 * clearly-marked stub (communityStub.ts), which never sends anything and returns
 * sample plans, so this is safe to ship as a preview. A "not connected" banner
 * makes that honest to the user.
 */
import { useEffect, useState } from 'react';
import { useShellStore } from '@/store/shell';
import { usePilotagePlannerStore, selectActivePlan } from '@/store/pilotagePlanner';
import {
  COMMUNITY_STUB_NOTICE,
  listCommunityPlans,
  shareByEmail,
  sharePublicly,
  ratePlan,
  importCommunityPlan,
  type CommunityPlanSummary,
} from '@/pilotage/communityStub';
import { Sheet } from './Sheet';

function Stars({ value, onRate }: { value: number; onRate?: (n: number) => void }) {
  return (
    <span aria-label={`${value} of 5 stars`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          onClick={onRate ? () => onRate(n) : undefined}
          disabled={!onRate}
          aria-label={`${n} star${n === 1 ? '' : 's'}`}
          style={{ background: 'none', border: 'none', padding: 0, cursor: onRate ? 'pointer' : 'default', color: n <= Math.round(value) ? '#e1b12c' : '#c9d1e0', fontSize: 15 }}
        >
          ★
        </button>
      ))}
    </span>
  );
}

export function PilotageCommunitySheet() {
  const activeSheet = useShellStore((s) => s.activeSheet);
  const open = activeSheet === 'pilotagecommunity';
  const closeSheet = useShellStore((s) => s.closeSheet);
  const showToast = useShellStore((s) => s.showToast);

  const plans = usePilotagePlannerStore((s) => s.plans);
  const activePlanId = usePilotagePlannerStore((s) => s.activePlanId);
  const importPlan = usePilotagePlannerStore((s) => s.importPlan);
  const activePlan = selectActivePlan({ plans, activePlanId });

  const [sort, setSort] = useState<'rating' | 'popularity'>('rating');
  const [list, setList] = useState<CommunityPlanSummary[]>([]);
  const [email, setEmail] = useState('');
  const [myRatings, setMyRatings] = useState<Record<string, number>>({});

  useEffect(() => {
    if (!open) return;
    let live = true;
    void listCommunityPlans(sort).then((r) => {
      if (live) setList(r);
    });
    return () => {
      live = false;
    };
  }, [open, sort]);

  async function doShareEmail() {
    if (!activePlan || !email.trim()) return;
    const r = await shareByEmail(activePlan.id, email.trim());
    showToast(r.message);
    setEmail('');
  }
  async function doSharePublic() {
    if (!activePlan) return;
    const r = await sharePublicly(activePlan.id);
    showToast(r.message);
  }
  async function doRate(id: string, stars: number) {
    setMyRatings((m) => ({ ...m, [id]: stars }));
    const r = await ratePlan(id, stars);
    showToast(r.message);
  }
  function load(id: string, activate: boolean) {
    const doc = importCommunityPlan(id);
    if (!doc) return;
    importPlan(doc, activate);
    if (activate) {
      closeSheet();
      showToast('Community plan loaded onto the chart');
    } else {
      showToast('Saved to your plans');
    }
  }

  return (
    <Sheet
      name="pilotagecommunity"
      title="Community plans"
      sub="Share your pilotage plans and use others'. A preview for now — nothing is sent yet."
    >
      <div style={{ border: '1px solid #e0b000', background: 'rgba(225,177,44,0.14)', borderRadius: 10, padding: '8px 10px', fontSize: 12, color: '#7a5b00', marginBottom: 10 }}>
        <b>Preview · not connected.</b> {COMMUNITY_STUB_NOTICE}
      </div>

      <label className="acp-fieldlabel">Share {activePlan ? `“${activePlan.name}”` : 'a plan'}</label>
      {activePlan ? (
        <>
          <div style={{ display: 'flex', gap: 6 }}>
            <input
              className="acp-text-input"
              style={{ flex: 1 }}
              type="email"
              inputMode="email"
              placeholder="Share with an email…"
              value={email}
              aria-label="Recipient email"
              onChange={(e) => setEmail(e.target.value)}
            />
            <button type="button" className="acp-btn" disabled={!email.trim()} onClick={() => void doShareEmail()}>
              Send
            </button>
          </div>
          <div className="acp-btn-row" style={{ marginTop: 6 }}>
            <button type="button" className="acp-btn" onClick={() => void doSharePublic()}>
              Share publicly
            </button>
          </div>
        </>
      ) : (
        <p className="acp-note">Load or build a plan first, then come back here to share it.</p>
      )}

      <label className="acp-fieldlabel" style={{ marginTop: 12 }}>Discover</label>
      <div className="acp-seg">
        <button type="button" className={sort === 'rating' ? 'on' : ''} onClick={() => setSort('rating')}>
          Top rated
        </button>
        <button type="button" className={sort === 'popularity' ? 'on' : ''} onClick={() => setSort('popularity')}>
          Most used
        </button>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
        {list.map((p) => (
          <div key={p.id} style={{ border: '1px solid rgba(127,127,127,0.28)', borderRadius: 12, padding: 10 }}>
            <b>{p.name}</b>
            <div style={{ fontSize: 12, opacity: 0.75 }}>{p.author} · {p.area}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, margin: '4px 0' }}>
              <Stars value={p.ratingAvg} />
              <span>{p.ratingAvg.toFixed(1)} ({p.ratingCount}) · {p.uses} uses</span>
            </div>
            <p style={{ fontSize: 12.5, margin: '2px 0 8px' }}>{p.description}</p>
            <div className="acp-btn-row">
              <button type="button" className="acp-btn primary" onClick={() => load(p.id, true)}>Load</button>
              <button type="button" className="acp-btn" onClick={() => load(p.id, false)}>Duplicate to my plans</button>
            </div>
            <div style={{ fontSize: 12, marginTop: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
              <span>Rate:</span>
              <Stars value={myRatings[p.id] ?? 0} onRate={(n) => void doRate(p.id, n)} />
              {myRatings[p.id] ? <span style={{ opacity: 0.6 }}>(not sent — preview)</span> : null}
            </div>
          </div>
        ))}
      </div>
    </Sheet>
  );
}
